import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { ReactComponent as FPSIcon } from '@/assets/icons/fps-big.svg';
import { HeaderTitle, Label, PageTitle, Space } from '@/components';
import { ApiTransactionAPIModelStatusEnum as TransactionAPIModelStatusEnum } from '@/generated/api/fps/core';
import { StatusLayout } from '@/layout';
import { makeSelectIsMobileView } from '@/model/global/selectors';
import ResultFields from '@/pages/withdrawal/pages/StatusPage/components/ResultFields';

import { useFormHeight } from './hooks';
import { FailedViewProps } from './types';

const selectIsMobile = makeSelectIsMobileView();

export const FailedView: React.FC<FailedViewProps> = ({ transaction, action, status, 'data-test': dataTest }) => {
  const isMobile = useSelector(selectIsMobile);
  const formHeight = useFormHeight({
    transaction,
    hasAction: !!action,
  });
  const description = useMemo(() => {
    if (status === TransactionAPIModelStatusEnum.Unknown) {
      return <FormattedMessage id="withdrawal.pages.result.failed.description.unknown" />;
    }
    if (status === TransactionAPIModelStatusEnum.Failed) {
      return <FormattedMessage id="withdrawal.pages.result.failed.description.failed" />;
    }
    if (status === TransactionAPIModelStatusEnum.Cancelled) {
      return <FormattedMessage id="withdrawal.pages.result.failed.description.cancelled" />;
    }
    return <FormattedMessage id="withdrawal.pages.result.failed.description.common" />;
  }, [status]);
  const title = useMemo(() => {
    if (status === TransactionAPIModelStatusEnum.Cancelled) {
      return <FormattedMessage id="withdrawal.pages.result.failed.title.cancelled" />;
    }
    return <FormattedMessage id="withdrawal.pages.result.failed.title.common" />;
  }, [status]);
  return (
    <StatusLayout
      data-test={dataTest && `${dataTest}-layout`}
      height={formHeight}
      minHeight={400}
      status={status === TransactionAPIModelStatusEnum.Cancelled ? 'failed' : 'awaiting'}
      header={{
        title: (
          <HeaderTitle
            data-test={dataTest && `${dataTest}-header`}
            title={
              transaction?.isFPS ? (
                <FormattedMessage id="withdrawal.pages.result.title" />
              ) : (
                <FormattedMessage id="demo.pages.transaction.common.title" />
              )
            }
            postfix={isMobile && transaction?.isFPS && <FPSIcon />}
          />
        ),
      }}
      footer={{
        component:
          action || (!isMobile && !transaction) ? (
            <Space
              direction="vertical"
              size={2}
              style={{ alignItems: 'center' }}
              data-test={dataTest && `${dataTest}-footer`}
            >
              {action}
              {!isMobile && !transaction && <FPSIcon />}
            </Space>
          ) : (
            <></>
          ),
      }}
    >
      <Space direction="vertical" size={2}>
        <PageTitle style={{ textAlign: 'center' }} data-test={dataTest && `${dataTest}-title`}>
          {title}
        </PageTitle>
        {transaction ? (
          <ResultFields
            data-test={dataTest && `${dataTest}-fields`}
            data={transaction}
            isCredit={transaction.isCredit}
          />
        ) : (
          <Label data-test={dataTest && `${dataTest}-message`} style={{ textAlign: 'center' }}>
            {description}
          </Label>
        )}
      </Space>
    </StatusLayout>
  );
};

export default FailedView;
